<template>
  <div>
    <!-- Desktop: table -->
    <v-simple-table
      v-if="$vuetify.breakpoint.mdAndUp"
      class="shared-accounts-table">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left" style="width: 260px">
              {{ $t('sharedAccounts.attributes.account') }}
            </th>
            <th class="text-left" style="width: 180px">
              {{ $t('sharedAccounts.filters.launcher') }}
            </th>
            <th class="text-left" style="min-width: 260px">
              {{ $t('sharedAccounts.attributes.games') }}
            </th>
            <th class="text-left" style="width: 120px">
              {{ $t('sharedAccounts.attributes.status') }}
            </th>
            <th class="text-right" style="width: 130px">
              {{ $t('miscellaneous.actions') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="account in accounts" :key="account.id">
            <!-- Account -->
            <td>
              <div class="d-flex align-center" style="max-width: 250px">
                <span
                  class="cell-truncate font-weight-medium"
                  :title="account.username">
                  {{ account.username }}
                </span>
                <v-tooltip v-if="sharedAcrossArenas(account)" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      x-small
                      class="ml-1 text--secondary flex-shrink-0"
                      v-bind="attrs"
                      v-on="on">
                      mdi-share-variant
                    </v-icon>
                  </template>
                  <span>
                    {{
                      $t('sharedAccounts.sharedAcrossArenas', {
                        count: account.arena_ids.length,
                      })
                    }}
                  </span>
                </v-tooltip>
              </div>
              <div
                v-if="showEmail(account)"
                class="cell-truncate caption text--secondary"
                style="max-width: 250px"
                :title="account.email">
                {{ account.email }}
              </div>
            </td>

            <!-- Launcher -->
            <td>
              <div class="d-flex align-center">
                <v-avatar
                  v-if="launcherIcon(account)"
                  size="20"
                  class="mr-2"
                  tile>
                  <v-img :src="launcherIcon(account)" contain />
                </v-avatar>
                <v-icon v-else small class="mr-2 text--secondary">
                  mdi-google-controller
                </v-icon>
                <span
                  class="cell-truncate"
                  style="max-width: 130px"
                  :title="launcherName(account)">
                  {{ launcherName(account) }}
                </span>
              </div>
            </td>

            <!-- Games -->
            <td class="py-2">
              <div class="d-flex align-center flex-wrap">
                <span
                  v-if="!account.games || !account.games.length"
                  class="text--secondary caption">
                  {{ $t('sharedAccounts.noGames') }}
                </span>
                <template
                  v-else-if="account.games.length <= gamesInlineThreshold">
                  <template v-for="game in account.games">
                    <v-tooltip v-if="isBanned(game)" :key="game.game_id" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          small
                          class="ma-1"
                          color="error"
                          text-color="white"
                          v-bind="attrs"
                          v-on="on">
                          <v-icon x-small left>mdi-cancel</v-icon>
                          {{ game.name }}
                        </v-chip>
                      </template>
                      <span>
                        {{ $t('sharedAccounts.bannedUntil') }}
                        {{ formatDate(new Date(game.banned_until), false) }}
                      </span>
                    </v-tooltip>
                    <v-chip
                      v-else
                      :key="game.game_id"
                      small
                      outlined
                      class="ma-1">
                      {{ game.name }}
                    </v-chip>
                  </template>
                </template>
                <template v-else>
                  <v-chip small label outlined class="ma-1">
                    <v-icon x-small left>mdi-controller</v-icon>
                    {{
                      $tc('sharedAccounts.gamesCount', account.games.length, {
                        count: account.games.length,
                      })
                    }}
                  </v-chip>
                  <v-chip
                    v-if="bannedCount(account)"
                    small
                    label
                    color="error"
                    text-color="white"
                    class="ma-1">
                    <v-icon x-small left>mdi-cancel</v-icon>
                    {{
                      $t('sharedAccounts.bannedCount', {
                        count: bannedCount(account),
                      })
                    }}
                  </v-chip>
                </template>
              </div>
            </td>

            <!-- Status -->
            <td>
              <div class="d-flex flex-column align-start" style="gap: 4px">
                <!-- Banned (account-level) takes priority -->
                <v-chip
                  v-if="account.banned"
                  small
                  color="error"
                  text-color="white">
                  <v-icon x-small left>mdi-cancel</v-icon>
                  {{ $t('sharedAccounts.accountBan.banned') }}
                </v-chip>
                <!-- Borrow state -->
                <template v-if="account.borrowed_by_session_id">
                  <v-tooltip v-if="localPcName(account)" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        small
                        color="indigo"
                        text-color="white"
                        class="font-weight-medium pc-chip"
                        v-bind="attrs"
                        v-on="on">
                        <v-icon x-small left>mdi-monitor</v-icon>
                        <span class="pc-name">{{ localPcName(account) }}</span>
                      </v-chip>
                    </template>
                    <span>
                      {{
                        $t('sharedAccounts.inUseOnPc', {
                          pc: localPcName(account),
                        })
                      }}
                    </span>
                  </v-tooltip>
                  <v-tooltip v-else-if="isOtherArena(account)" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        small
                        color="warning"
                        text-color="white"
                        v-bind="attrs"
                        v-on="on">
                        <v-icon x-small left>mdi-map-marker</v-icon>
                        {{ $t('sharedAccounts.otherArena') }}
                      </v-chip>
                    </template>
                    <span>
                      {{
                        $t('sharedAccounts.inUseInArena', {
                          arena: account.borrowed_in_arena_id,
                        })
                      }}
                    </span>
                  </v-tooltip>
                  <v-chip v-else small color="warning" text-color="white">
                    <v-icon x-small left>mdi-account-arrow-right</v-icon>
                    {{ $t('sharedAccounts.inUse') }}
                  </v-chip>
                </template>
                <!-- Free only when neither banned nor borrowed -->
                <v-chip
                  v-else-if="!account.banned"
                  small
                  outlined
                  color="success">
                  {{ $t('sharedAccounts.free') }}
                </v-chip>
              </div>
            </td>

            <!-- Actions -->
            <td class="text-right text-no-wrap">
              <v-tooltip color="transparent" top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    @click="$emit('history', { account })"
                    class="glow ml-2"
                    depressed
                    x-small
                    fab>
                    <v-icon small>mdi-clipboard-text-clock-outline</v-icon>
                  </v-btn>
                </template>
                <v-card class="elevation-2 px-4 py-2">
                  <span>{{ $t('sharedAccounts.history.title') }}</span>
                </v-card>
              </v-tooltip>
              <v-tooltip color="transparent" top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    @click="$emit('manage', { account })"
                    color="primary"
                    class="glow ml-2"
                    depressed
                    x-small
                    dark
                    fab>
                    <v-icon small dark>mdi-cog</v-icon>
                  </v-btn>
                </template>
                <v-card class="elevation-2 px-4 py-2">
                  <span>{{ $t('sharedAccounts.manage') }}</span>
                </v-card>
              </v-tooltip>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <!-- Mobile: stacked cards -->
    <div v-else>
      <v-card
        v-for="account in accounts"
        :key="account.id"
        outlined
        class="mb-2">
        <div class="pa-3">
          <div class="d-flex align-start">
            <div class="flex-grow-1" style="min-width: 0">
              <div class="d-flex align-center">
                <span
                  class="cell-truncate font-weight-medium"
                  :title="account.username">
                  {{ account.username }}
                </span>
                <v-icon
                  v-if="sharedAcrossArenas(account)"
                  x-small
                  class="ml-1 text--secondary flex-shrink-0">
                  mdi-share-variant
                </v-icon>
              </div>
              <div
                v-if="showEmail(account)"
                class="cell-truncate caption text--secondary">
                {{ account.email }}
              </div>
            </div>
            <v-btn
              class="glow ml-2 flex-shrink-0"
              depressed
              x-small
              fab
              @click="$emit('history', { account })">
              <v-icon small>mdi-clipboard-text-clock-outline</v-icon>
            </v-btn>
            <v-btn
              color="primary"
              class="glow ml-2 flex-shrink-0"
              depressed
              x-small
              dark
              fab
              @click="$emit('manage', { account })">
              <v-icon small dark>mdi-cog</v-icon>
            </v-btn>
          </div>

          <div class="d-flex align-center mt-2">
            <v-avatar v-if="launcherIcon(account)" size="18" class="mr-2" tile>
              <v-img :src="launcherIcon(account)" contain />
            </v-avatar>
            <v-icon v-else x-small class="mr-2 text--secondary">
              mdi-google-controller
            </v-icon>
            <span class="caption">{{ launcherName(account) }}</span>
            <v-spacer />
            <!-- Status (compact, no tooltip on mobile) -->
            <v-chip
              v-if="account.banned"
              x-small
              color="error"
              text-color="white"
              class="ml-1">
              <v-icon x-small left>mdi-cancel</v-icon>
              {{ $t('sharedAccounts.accountBan.banned') }}
            </v-chip>
            <template v-if="account.borrowed_by_session_id">
              <v-chip
                v-if="localPcName(account)"
                x-small
                color="indigo"
                text-color="white"
                class="font-weight-medium ml-1">
                <v-icon x-small left>mdi-monitor</v-icon>
                {{ localPcName(account) }}
              </v-chip>
              <v-chip
                v-else-if="isOtherArena(account)"
                x-small
                color="warning"
                text-color="white"
                class="ml-1">
                <v-icon x-small left>mdi-map-marker</v-icon>
                {{ $t('sharedAccounts.otherArena') }}
              </v-chip>
              <v-chip
                v-else
                x-small
                color="warning"
                text-color="white"
                class="ml-1">
                {{ $t('sharedAccounts.inUse') }}
              </v-chip>
            </template>
            <v-chip
              v-else-if="!account.banned"
              x-small
              outlined
              color="success"
              class="ml-1">
              {{ $t('sharedAccounts.free') }}
            </v-chip>
          </div>

          <div v-if="account.games && account.games.length" class="mt-2">
            <v-chip x-small label outlined class="mr-1">
              <v-icon x-small left>mdi-controller</v-icon>
              {{
                $tc('sharedAccounts.gamesCount', account.games.length, {
                  count: account.games.length,
                })
              }}
            </v-chip>
            <v-chip
              v-if="bannedCount(account)"
              x-small
              label
              color="error"
              text-color="white">
              <v-icon x-small left>mdi-cancel</v-icon>
              {{
                $t('sharedAccounts.bannedCount', {
                  count: bannedCount(account),
                })
              }}
            </v-chip>
          </div>
          <div v-else class="mt-2 caption text--secondary">
            {{ $t('sharedAccounts.noGames') }}
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import formatDate from '@/mixins/format-date'
import { getBorrowStatus, isGameActivelyBanned } from '@/utils/shared-accounts'

// At/under this many games we render chips inline; above it we collapse to a
// summary that opens the per-account games manager dialog.
const GAMES_INLINE_THRESHOLD = 10

export default {
  name: 'SharedAccountsTable',
  mixins: [formatDate],
  props: {
    accounts: { type: Array, default: () => [] },
    currentArenaId: { type: [Number, String], default: null },
    // Map of session id -> PC hostname for the current arena's active sessions.
    sessionHostnames: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      gamesInlineThreshold: GAMES_INLINE_THRESHOLD,
    }
  },
  methods: {
    isBanned(game) {
      return isGameActivelyBanned(game)
    },
    bannedCount(account) {
      if (!account || !Array.isArray(account.games)) return 0
      return account.games.filter((g) => isGameActivelyBanned(g)).length
    },
    sharedAcrossArenas(account) {
      return Array.isArray(account.arena_ids) && account.arena_ids.length > 1
    },
    showEmail(account) {
      const email = (account.email || '').trim()
      if (!email) return false
      return (
        email.toLowerCase() !== (account.username || '').trim().toLowerCase()
      )
    },
    launcherName(account) {
      return (
        (account.app && account.app.name) ||
        this.$t('sharedAccounts.unknownLauncher')
      )
    },
    launcherIcon(account) {
      return (account.app && account.app.icon) || ''
    },
    borrowStatus(account) {
      return getBorrowStatus(
        account,
        this.sessionHostnames,
        this.currentArenaId,
      )
    },
    isOtherArena(account) {
      return this.borrowStatus(account).otherArena
    },
    localPcName(account) {
      return this.borrowStatus(account).pcName
    },
  },
}
</script>

<style scoped>
/* Truncate long usernames/emails/launchers; full value via native title. */
.cell-truncate {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

/* "In use on a PC here" is the distinct, notable state. */
.pc-chip {
  box-shadow: 0 1px 4px rgba(63, 81, 181, 0.45);
}

.pc-name {
  display: inline-block;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

/* Standard row-action button glow (matches EditButton/DeleteButton). */
.glow {
  transition-duration: 0.3s;
  transition-property: box-shadow;
  box-shadow: 0 0 1px transparent;
}
.glow:hover {
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.6) !important;
}
</style>
